<template>
  <v-card class="admin application-notes">
    <v-card-title flat class="py-0">
      <v-badge right>
        <v-icon left>mdi-text</v-icon> <span class="title">Notes</span>
        <span slot="badge">{{ orderedActivities.length }}</span>
      </v-badge>
      <v-spacer />
      <div class="text-xs-right">
        <v-switch v-model="allOpportunities" label="All" />
      </div>
    </v-card-title>
    <v-divider />

    <div
      class="py-1"
      style="height: 300px; overflow-y: scroll; overflow-x: hidden;"
      ref="activities-container"
    >
      <div v-if="!orderedActivities[0]">No notes yet</div>

      <div v-for="(group, day) in dateGroupedActivities" :key="`activity-group-${day}`">
        <v-chip small class="caption">
          {{ new Date(day) | date('d MMM yyyy') }}
        </v-chip>

        <div
          v-for="(activity, i) in group"
          :key="`activity-${i}`"
          class="container"
          :class="{ alt: isMe(activity.initiator), muted: isFromOtherOpportunity(activity) }"
        >
          <div class="activity-avatar hidden-xs-only">
            <v-tooltip bottom>
              <v-avatar slot="activator" size="32">
                <v-img
                  contain
                  :class="{ 'v-avatar--grayscale': isFromOtherOpportunity(activity) }"
                  max-width="32"
                  :src="activity.initiator.avatar || '/img/missing/avatars/original.png'"
                />
              </v-avatar>
              <span> {{ activity.initiator.fullName }} </span>
            </v-tooltip>
          </div>

          <div class="activity-content text-xs-left" :class="{ compact: isNote(activity) }">
            <div
              v-if="isEvent(activity)"
              :class="
                isFromOtherOpportunity(activity) ? 'grey white--text' : 'secondary white--text'
              "
            >
              <div v-if="activity.content" class="muted">
                Applied at {{ activity.content }}
              </div>
              <div v-else class="muted">
                Moved to {{ activity.status }} <span v-if="activity.reason">({{activity.reason}})</span>
              </div>
            </div>

            <div v-if="isRating(activity)" class="grey">
              <v-layout align-center class="pa-0">
                <v-icon medium class="pa-2">
                  mdi-star
                </v-icon>
                <div class="headline pt-1 muted">{{ (activity.value / 2).toFixed(1) }}</div>
              </v-layout>
            </div>

            <div v-if="isFeedbackRequest(activity)">
              <v-layout align-center class="pa-0">
                <v-icon
                  medium
                  v-if="activity.isPositive && activity.isPositive == true"
                  class="pa-2"
                  :color="
                    isFromOtherOpportunity(activity) ? 'grey grey--text' : 'success white--text'
                  "
                >
                  mdi-thumb-up
                </v-icon>
                <v-icon
                  medium
                  v-else-if="activity.hasOwnProperty('isPositive') && activity.isPositive == false"
                  class="pa-2"
                  :color="
                    isFromOtherOpportunity(activity) ? 'grey grey--text' : 'error white--text'
                  "
                >
                  mdi-thumb-down
                </v-icon>
                <v-icon medium class="pa-2 muted" v-else>mdi-share-variant</v-icon>
                <div>
                  <span v-if="activity.response"> {{ activity.response }} </span>
                  <v-tooltip bottom v-if="activity.responder">
                    <v-avatar slot="activator" size="32">
                      <v-img
                        contain
                        :src="activity.responder.avatar || '/img/missing/avatars/original.png'"
                        :class="{ 'v-avatar--grayscale': isFromOtherOpportunity(activity) }"
                      />
                    </v-avatar>
                    <span> {{ activity.responder.fullName }} </span>
                  </v-tooltip>
                </div>
              </v-layout>
            </div>

            <div v-if="isFlag(activity)" class="secondary white--text">
              <v-layout align-center class="pa-0">
                <div class="px-0">
                  <Flag v-if="activity.key" :value="activity" />
                </div>
              </v-layout>
            </div>

            <div
              v-if="isNote(activity)"
              class="admin-note text-xs-left"
              :class="isFromOtherOpportunity(activity) ? 'grey white--text' : 'primary white--text'"
            >
              <span>
                <v-icon :color="isFromOtherOpportunity(activity) ? 'grey' : 'primary white--text'">
                  mdi-triangle
                </v-icon>
              </span>
              <div :class="{ muted: isFromOtherOpportunity(activity) }">
                <markdown :html="false" :source="activity.content" class="markdown" />
              </div>
            </div>

            <div
              class="text-xs-right time-stamp"
              :class="{ 'white--text': isNote(activity) && !isFromOtherOpportunity(activity) }"
              style="font-size: 9px;"
            >
              <span v-if="isFromOtherOpportunity(activity) && activity.opportunity && activity.opportunity.name">
                {{ activity.opportunity.name }}
              </span>
              <span> {{ new Date(activity.createdAt) | date('HH:mm') }} </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="savingNote && savingNote.length">
        <div class="caption" color="grey">
          {{ new Date() | date('d MMM yyyy') }}
        </div>

        <div class="container alt">
          <div class="activity-avatar hidden-xs-only">
            <v-progress-circular size="32" indeterminate color="success">
              <v-avatar size="30">
                <img
                  :src="user.avatar || 'img/missing/avatars/original.png'"
                />
              </v-avatar>
            </v-progress-circular>
          </div>

          <div class="activity-content text-xs-left compact" style="max-width: 100%;">
            <div class="client-note text-xs-left primary white--text">
              <span> <v-icon color="primary">mdi-triangle</v-icon> </span>
              <markdown :html="false" :source="savingNote" class="markdown" />
            </div>

            <div class="text-xs-right time-stamp white--text" style="font-size: 9px;">
              <span>Saving...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <can I="create" a="Note">
      <v-divider />
      <v-card-actions class="px-0 py-0">
        <v-textarea box
          placeholder="New Note"
          v-model="newNote"
          :rows="1"
          append-icon="mdi-send"
          @click:append="saveNote"
          @keyup.enter.exact="saveNote"
          :value="true ? 'true' : 'false'"
          color="primary"
          style="margin-bottom: -27px; border-radius: 0px;"
        />
      </v-card-actions>
    </can>
  </v-card>
</template>

<script>
// Components
import Markdown from 'vue-markdown'
import Flag from '@/components/flags/Flag'

// Helpers
import colors from '@/data/colors'
import _ from 'lodash'

export default {
  props: { application: Object },

  mounted() {
    this.scrollDown()
  },

  components: { Flag, Markdown },

  data() {
    return {
      newNote: '',
      savingNote: '',
      allOpportunities: true,
      colors,
    }
  },

  computed: {
    user() {
      return this.$store.getters['auth/user']
    },

    orderedActivities() {
      if (!this.application || !this.application.activities || !this.application.activities[0])
        return []

      const activities = JSON.parse(JSON.stringify(this.application.activities))

      return activities
        .filter(a => (this.allOpportunities ? true : !this.isFromOtherOpportunity(a)))
        .sort(({ createdAt: a }, { createdAt: b }) => {
          if (new Date(a) > new Date(b)) return 1
          if (new Date(a) < new Date(b)) return -1
          return 0
        })
    },

    dateGroupedActivities() {
      if (!this.orderedActivities[0]) return []

      return _.groupBy(this.orderedActivities, a => a.createdAt.substring(0, 10))
    },
  },

  watch: {
    application: {
      deep: true,
      handler: function(value) {
        this.scrollDown()
      },
    },
  },

  methods: {
    isLast: (index, array) => array.length === index + 1,

    isEvent: activity => activity.type === 'Application event',

    isRating: activity => activity.type === 'Application rating',

    isFeedbackRequest: activity => activity.type === 'Feedback request',

    isFlag: activity => activity.type === 'Flag',

    isNote: activity => activity.type === 'Application note',

    isMe(user) {
      if (_.get(this, 'user.id') && _.get(user, 'id')) return this.user.id === user.id
    },

    isFromOtherOpportunity(activity) {
      if (_.get(this.application, 'opportunity.id') && _.get(activity, 'opportunity.id')) {
        return this.application.opportunity.id !== activity.opportunity.id
      }
      return false
    },

    scrollDown() {
      this.$nextTick(() => {
        const container = this.$refs['activities-container']
        if (container) container.scrollTop = container.scrollHeight
      })
    },

    saveNote() {
      this.savingNote = this.newNote.length > 0 ? this.newNote.trim() : this.newNote
      this.newNote = ''
      if (this.savingNote.length > 0) {
        this.$nextTick(() => {
          this.scrollDown()
        })
        this.$http
          .post('admin/application_notes', {
            application: this.application.id,
            note: this.savingNote,
          })
          .then(
            response => {
              this.$store.dispatch('snackbarSuccess', 'Note Saved')
              return this.$store
                .dispatch('admin/applications/fetch', this.application.id)
                .then(response => {
                  this.savingNote = ''
                  this.scrollDown()
                })
            },
            errors => {
              this.$store.dispatch('snackbarError', 'Note Failed')
              this.savingNote = ''
              this.scrollDown()
            }
          )
      }
    },
  },
}
</script>

<style lang="scss">
.admin.application-notes {
  .muted {
    opacity: 0.7;
  }

  .container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0.1rem 0.7rem;
    margin: 0 auto;
    max-width: 100%;

    .activity-avatar {
      padding-right: 0.8rem;
      padding-left: 0;
    }

    .v-avatar--grayscale {
      opacity: 0.6;
      filter: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'><filter id='grayscale'><feColorMatrix type='matrix' values='0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0 0 0 1 0'/></filter></svg>#grayscale"); /* Firefox 10+, Firefox on Android */
      filter: grayscale(100%);
      -moz-filter: grayscale(100%);
      -ms-filter: grayscale(100%);
      -o-filter: grayscale(100%);
      filter: gray; /* IE6-9 */
      -webkit-filter: grayscale(100%); /* Chrome 19+, Safari 6+, Safari 6+ iOS */
    }

    & .activity-content {
      box-sizing: border-box;
      position: relative;
      padding: 0.3rem 10% 0.3rem 0;
      min-width: 50%;

      > div,
      > div:first-child {
        border-radius: 5px;
        line-height: 1rem;
        padding: 0.3rem 1.0rem 0.8rem 0.8rem;
      }

      &.compact {
        flex-grow: unset;
      }

      & .time-stamp {
        opacity: 0.7;
        padding: 0.4rem 0.2rem 0.2rem 0.4rem;
        margin-top: -1.5rem;
      }

      & div.admin-note {
        border-radius: 5px;
        padding: 0.1rem 1rem 0.3rem 0rem;
        opacity: 1;

        > div {
          padding: 0 1.8rem 0.5rem 0.8rem;
        }

        & div.markdown > p {
          margin-bottom: 0px;
        }

        & .v-icon {
          position: absolute;
          transform: translateY(-70%) rotate(30deg);
          top: 50%;
          left: -0.5rem;
        }
      }
    }
  }

  & .container.alt {
    flex-direction: row-reverse;

    & .activity-avatar {
      padding-left: 0.8rem;
      padding-right: 0;
    }

    & .activity-content {
      padding-left: 10%;
      padding-right: 0%;
    }

    & div.admin-note {
      & .v-icon {
        position: absolute;
        transform: translateY(-70%) rotate(-30deg);
        top: 50%;
        right: -0.5rem;
        left: auto;
      }
    }
  }
}
</style>
